/**
 * PLAT-11385 / PLAT-11373 — the two reads the dub popover needs before it can
 * offer a direction (a statement of how the lines should be delivered):
 *
 * 1. what each voice's text-to-speech model declares it can be asked for, and
 * 2. which speakers a selected section is actually spoken by.
 *
 * Two more were added for PLAT-11383 / PLAT-11797 / PLAT-11798: whether the
 * numeric and on/off controls panel is switched on for this person, and where
 * the controls should start — the last run's direction, plus the words of the
 * single line a markers editor would be offered over.
 *
 * All of them answer before anything is typed. What is offered follows the models'
 * declarations and nothing else, so a voice that can take no direction shows no
 * direction control at all.
 */
import { dropSuperseded } from '@src/editor/dubbing/DubPlacementService';
import {
  markersSpanLines,
  previousDirection,
} from '@src/services/dubbing/directionPrefill';
import {
  directionModelIds,
  summarizeDirectionSupport,
} from '@src/services/dubbing/directionSupport';
import { useDatoClient, useStore } from '@src/stores-v2/StoreContext';
import { isAbortError } from '@src/utility/error';
import { computed } from 'mobx';
import { useEffect, useMemo, useState } from 'react';

import { useDubManifestFetch } from './useDubbedLanguages';

import type {
  DubDirection,
  DubManifestDTO,
  DubModelCapabilityDTO,
} from '@src/repositories/DubbingRepository';
import type { MarkersLine } from '@src/services/dubbing/directionPrefill';
import type {
  DirectionSupport,
  DirectionVoice,
} from '@src/services/dubbing/directionSupport';

/** What the popover learns about the run's voices. */
export interface DubDirectionCapabilityRead {
  /** False while the capability request is out; nothing is offered until it lands. */
  settled: boolean;
  /** The folded answer once settled; `null` before that. */
  support: DirectionSupport | null;
  /**
   * True when the capability request itself could not answer. Nothing is
   * offered in that case either: an unanswered question is not permission.
   */
  failed: boolean;
}

/**
 * What the given voices can be asked for.
 *
 * `voices` must be a stable value (memoized by the caller); the request is
 * keyed on the distinct model strings, so re-resolving the same voices does not
 * re-ask. A voice whose catalog record names no model is asked about by nobody
 * and lands as "unknown", which offers nothing.
 */
export function useDubDirectionCapability(
  voices: readonly DirectionVoice[],
): DubDirectionCapabilityRead {
  const datoClientStore = useDatoClient();
  const modelIdsKey = useMemo(
    () => directionModelIds(voices).join(','),
    [voices],
  );
  // The answer is stored WITH the question it answers. Without that pairing
  // there is a render in which the run's voices have already changed but the
  // effect that re-asks has not run yet, so the previous answer is folded
  // together with the new voices — every new model is missing from it, which
  // reads as "these voices cannot be directed" and paints that sentence on
  // screen for one frame. Comparing the two keys makes a stale answer count as
  // "still asking", which is what it is.
  const [answer, setAnswer] = useState<{
    key: string;
    rows: DubModelCapabilityDTO[];
    failed: boolean;
  } | null>(null);

  useEffect(() => {
    // No model is named by any voice in the run: there is nothing to ask, and
    // the answer is already "nothing declared". Settling here rather than
    // hanging keeps the popover from waiting on a request never made.
    if (!modelIdsKey) {
      setAnswer({ key: '', rows: [], failed: false });
      return undefined;
    }
    const controller = new AbortController();
    void datoClientStore.dubbingRepository
      .getDubCapabilities({
        modelIds: modelIdsKey.split(','),
        signal: controller.signal,
      })
      .then(result => {
        if (controller.signal.aborted) return;
        setAnswer({ key: modelIdsKey, rows: result.models, failed: false });
      })
      .catch((error: unknown) => {
        if (isAbortError(error) || controller.signal.aborted) return;
        setAnswer({ key: modelIdsKey, rows: [], failed: true });
      });
    return () => controller.abort();
  }, [modelIdsKey, datoClientStore]);

  const current = answer?.key === modelIdsKey ? answer : null;

  const support = useMemo(
    () => (current ? summarizeDirectionSupport(voices, current.rows) : null),
    [current, voices],
  );

  return {
    settled: current !== null,
    support,
    failed: current?.failed === true,
  };
}

/** Which speakers a selected section is spoken by. */
export interface DubSpanSpeakersRead {
  /**
   * The transcript speaker labels (`spk_X` / `override_<id>`) the selected
   * lines are recorded under, or `null` when the section is unknown — either
   * nothing is selected, or the manifest could not be read. `null` deliberately
   * means "assume the whole run", which can only ever offer less.
   */
  labels: string[] | null;
  /** False while the manifest request is out. */
  settled: boolean;
}

/**
 * The speakers of a section re-dub's own lines, read from the dub manifest —
 * the stored map of which line was voiced by which speaker, which is the only
 * record of that connection on the client.
 *
 * Without this, a section spoken by one speaker would be judged against every
 * speaker in the story, and a section that can be directed would be told it
 * cannot.
 */
export function useDubSpanSpeakers(params: {
  storyId: string;
  language: string;
  /** The run's `segmentIds`; absent for a whole-language run. */
  segmentIds?: readonly string[];
}): DubSpanSpeakersRead {
  const { storyId, language } = params;
  const segmentIdsKey = (params.segmentIds ?? []).join(',');
  const fetchManifest = useDubManifestFetch();
  const [labels, setLabels] = useState<string[] | null>(null);
  // Unsettled from the very first render when there IS a section to look up,
  // so the popover never spends a render judging the whole story's voices and
  // asking about models the section does not use.
  const [settled, setSettled] = useState(!segmentIdsKey);

  useEffect(() => {
    if (!segmentIdsKey) {
      setLabels(null);
      setSettled(true);
      return undefined;
    }
    const wanted = new Set(segmentIdsKey.split(','));
    const controller = new AbortController();
    setSettled(false);
    void fetchManifest({ storyId, language })
      .then(resolved => {
        if (controller.signal.aborted) return;
        const live = resolved
          ? dropSuperseded(resolved.manifest.segments ?? []).live
          : [];
        const found = new Set<string>();
        for (const segment of live) {
          if (wanted.has(segment.segmentId)) found.add(segment.speaker);
        }
        // An empty answer is not an answer: a section the manifest has no
        // recording for says nothing about who speaks it, so fall back to the
        // whole run rather than to "no voices at all".
        setLabels(found.size > 0 ? Array.from(found) : null);
        setSettled(true);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setLabels(null);
        setSettled(true);
      });
    return () => controller.abort();
  }, [storyId, language, segmentIdsKey, fetchManifest]);

  return { labels, settled };
}

/**
 * PLAT-11797 — is the panel of a voice model's own numeric and on/off controls
 * switched on for this person?
 *
 * A LaunchDarkly flag, default off, following `enableAiVoices` exactly. Read
 * here rather than passed in by each of the three screens that open the dub
 * popover, so no screen can forget the gate and show the panel by accident.
 *
 * Deliberately tolerant of a store that carries no flags at all: the answer
 * then is the flag's own default, which is off.
 */
export function useDubSettingsPanelEnabled(): boolean {
  const store = useStore();
  return computed(
    () => store.featureFlagsStore?.flags?.enableDubVoiceSettings === true,
  ).get();
}

/** What the popover starts the direction controls from. */
export interface DubDirectionPrefillRead {
  /** False while the manifest request is out. */
  settled: boolean;
  /**
   * The direction the last run recorded, or `null` when this language has no
   * dub yet, the dub was never directed, or the read failed. `null` means
   * "start from the model's own declared values".
   */
  previous: DubDirection | null;
  /**
   * The lines this run covers, in spoken order, for the markers editor to
   * start from (PLAT-12198). `null` for a run the editor is not offered over.
   */
  lines: MarkersLine[] | null;
  /**
   * PLAT-12023 — the stored dub itself, for a speaker's preview to read their
   * words from (`previewSpanText`). `null` when there is no dub or the read
   * failed, which only means the preview has no words.
   */
  manifest: DubManifestDTO | null;
}

/**
 * PLAT-11383 / PLAT-11798 — the previous run's direction, and the lines a
 * markers editor would be offered over (PLAT-12198).
 *
 * Both come from the same stored dub, so they are one read. It is a SECOND read
 * of that dub alongside `useDubSpanSpeakers`, deliberately: that read gates
 * whether any direction control is offered at all, and folding this one into it
 * would make the whole direction block wait on a question that only decides
 * where its fields START. A failure here never hides a control; it only means
 * the controls begin at the model's own declared values.
 */
export function useDubDirectionPrefill(params: {
  storyId: string;
  language: string;
  /** The run's line ids; absent for a whole-language run. */
  segmentIds?: readonly string[];
  /** False keeps the read from being made at all (nothing would use it). */
  enabled?: boolean;
  /**
   * PLAT-12080 — bumped when a take lands while the controls stay on
   * screen, so they re-seed from the landed take rather than the one before.
   */
  reloadToken?: number;
}): DubDirectionPrefillRead {
  const { storyId, language, enabled = true, reloadToken = 0 } = params;
  const segmentIdsKey = (params.segmentIds ?? []).join(',');
  const fetchManifest = useDubManifestFetch();
  const [read, setRead] = useState<{
    previous: DubDirection | null;
    lines: MarkersLine[] | null;
    manifest: DubManifestDTO | null;
  }>({ previous: null, lines: null, manifest: null });
  const [settled, setSettled] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setRead({ previous: null, lines: null, manifest: null });
      setSettled(true);
      return undefined;
    }
    const controller = new AbortController();
    setSettled(false);
    const ids = segmentIdsKey ? segmentIdsKey.split(',') : undefined;
    void fetchManifest({ storyId, language })
      .then(resolved => {
        if (controller.signal.aborted) return;
        const manifest = resolved?.manifest ?? null;
        setRead({
          // Asked for THIS run's span: a run over one line starts from that
          // line's own last direction, not from the newest one anywhere in the
          // dub, which could belong to a different speaker's line entirely.
          previous: previousDirection(manifest, ids),
          lines: markersSpanLines(manifest, ids),
          manifest,
        });
        setSettled(true);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // A dub whose record could not be read starts the controls where the
        // model itself starts them. Nothing is hidden and nothing is invented.
        setRead({ previous: null, lines: null, manifest: null });
        setSettled(true);
      });
    return () => controller.abort();
  }, [enabled, storyId, language, segmentIdsKey, reloadToken, fetchManifest]);

  return {
    settled,
    previous: read.previous,
    lines: read.lines,
    manifest: read.manifest,
  };
}
